import { ArrowLeft, FolderKanban, GitCommitHorizontal, History, IdCard, PenLine } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useApp } from "@/lib/AppContext";
import { ApiError, type BeliefAssertion, type EntityType, type SubjectPageResponse } from "@/lib/api";
import { formatCost, formatDate, formatRelative } from "@/lib/format";
import { subjectKindMeta } from "@/lib/subjectKinds";
import { Belief, sourceHref } from "@/components/Belief";
import { AddBelief, CorrectableBelief } from "@/components/BeliefEditor";
import { entityHref, RelationsPanel } from "@/components/RelationsPanel";

/**
 * UI-10 — one subject, as the world view currently holds it.
 *
 * Everything on this page is read from the belief store. The wiki file it corresponds to is
 * *named* in the right rail and its commit is named in the footer, but neither is opened:
 * the repository is a publish target (WORLD-5), and the way that stays true is that this
 * screen has no path to it at all.
 *
 * The provenance disclosure under each belief is the world-view half of UI-7. It reveals the
 * insights behind the belief and links each one back to the source it was read from, with
 * `?insight=` so the recording detail highlights the lines it was drawn from — the same
 * address the review queue and the insight cards already use (M5.3).
 *
 * **Not read-only any more (UI-20).** The page carries the owner's hand the way the dossiers do:
 * add a belief, correct one, draw a relation. The About section is the entity's own description,
 * rendered first and edited where the entity is (WORLD-9), and the generated paragraph is shown
 * only where there is no description to show instead.
 */
export function WorldViewSubjectPage() {
  const { slug = "" } = useParams();
  const { api, entities, refreshWorldView } = useApp();

  const [answer, setAnswer] = useState<SubjectPageResponse | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [showSuperseded, setShowSuperseded] = useState(false);

  const load = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true);
    try {
      setAnswer(await api.getSubject(slug));
      setProblem(null);
    } catch (failure) {
      setAnswer(null);
      setProblem(
        failure instanceof ApiError && failure.status === 404
          ? "There is no subject at that address."
          : "That subject could not be read.",
      );
    } finally {
      setLoading(false);
    }
  }, [api, slug]);

  useEffect(() => {
    void load();
  }, [load]);

  // An owner's edit reloads the page quietly — no "Loading…" flash under the cursor — and the
  // index, whose card counts and summary line just moved with it.
  const reload = useCallback(async () => {
    await load(true);
    refreshWorldView();
  }, [load, refreshWorldView]);

  // The toggle is per subject: carrying "show superseded" from one page to the next would
  // be a small, confusing kind of memory, the same reason the navigator clears its filter.
  useEffect(() => setShowSuperseded(false), [slug]);

  if (loading) {
    return <p className="px-[var(--space-8)] py-[var(--space-8)] text-[length:var(--text-sm)] text-[var(--text-secondary)]">Loading…</p>;
  }

  if (!answer) {
    return (
      <div className="flex flex-col items-start gap-3 px-[var(--space-8)] py-[var(--space-8)]">
        <p role="alert" className="text-[length:var(--text-sm)] text-[var(--warning)]">
          {problem}
        </p>
        <BackLink />
      </div>
    );
  }

  const { page, git } = answer;
  const meta = subjectKindMeta(page.kind);
  const entityId = page.entityId;
  const assert = async (input: BeliefAssertion) => {
    if (entityId) await api.assertBelief(entityId, input);
    else await api.assertSubjectBelief(page.slug, input);
    await reload();
  };
  const Icon = meta.icon;
  const current = page.sections.reduce((total, section) => total + section.beliefs.length, 0);

  return (
    <div className="grid grid-cols-[minmax(0,1fr)_208px] gap-[var(--space-8)] px-[var(--space-8)] py-[var(--space-8)]">
      <div className="flex min-w-0 flex-col gap-[var(--space-6)]">
        <header className="flex flex-col gap-[var(--space-2)]">
          <BackLink />

          <div className="flex flex-wrap items-center gap-3">
            <span className="flex items-center gap-1.5 text-[length:var(--text-2xs)] font-semibold uppercase tracking-[var(--tracking-wider)] text-[var(--text-muted)]">
              <Icon size={12} strokeWidth={2.5} />
              {meta.label}
            </span>
            <span className="text-[length:var(--text-2xs)] text-[var(--text-muted)]">
              Updated {formatRelative(page.updatedAt)}
            </span>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-[length:var(--text-2xl)] font-extrabold tracking-[var(--tracking-snug)] text-[var(--text-primary)]">
              {page.title}
            </h1>

            {/* UI-11's other direction: a subject that *is* a person opens their dossier.
                Conditional rather than always drawn, because a subject need not be an
                entity at all — `topic` pages have nobody to link to. */}
            {page.isPerson && page.entityId && (
              <Link
                to={`/people/${page.entityId}`}
                className="inline-flex items-center gap-1.5 rounded-full border border-[var(--border-default)] px-2.5 py-0.5 text-[length:var(--text-2xs)] font-semibold text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
              >
                <IdCard size={12} strokeWidth={2} />
                Dossier
              </Link>
            )}

            {/* UI-19 — the same room, for an initiative's own dossier. */}
            {page.kind === "initiative" && page.entityId && (
              <Link
                to={entityHref("initiative", page.entityId)}
                className="inline-flex items-center gap-1.5 rounded-full border border-[var(--border-default)] px-2.5 py-0.5 text-[length:var(--text-2xs)] font-semibold text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
              >
                <FolderKanban size={12} strokeWidth={2} />
                Dossier
              </Link>
            )}
          </div>

          {!page.about && page.summary && (
            <p className="max-w-[62ch] text-[length:var(--text-md)] text-[var(--text-secondary)]">{page.summary}</p>
          )}
        </header>

        {/* WORLD-9 — what the owner wrote, first, as a section of its own. Verbatim: line breaks
            kept, nothing reworded. It is edited where the entity is, because it is the entity's. */}
        {page.about && (
          <section id="about" className="flex flex-col gap-[var(--space-2)]">
            <div className="flex flex-wrap items-baseline gap-2.5">
              <h2 className="text-[length:var(--text-lg)] font-bold text-[var(--text-primary)]">About</h2>
              {entityId && (
                <Link
                  to={`/entities/${entityId}`}
                  className="inline-flex items-center gap-1 text-[length:var(--text-2xs)] text-[var(--text-muted)] hover:text-[var(--vl-bridge-soft)]"
                >
                  <PenLine size={11} strokeWidth={2} />
                  Written by you — edit
                </Link>
              )}
            </div>
            <p className="max-w-[62ch] whitespace-pre-line text-[15px] leading-[1.7] text-[var(--text-primary)]">
              {page.about}
            </p>
          </section>
        )}

        {current === 0 && page.superseded.length === 0 && (
          <p className="text-[length:var(--text-sm)] text-[var(--text-secondary)]">
            Nothing is held on this subject yet. Add what you know below, or let it fill in as sources mention it.
          </p>
        )}

        {page.sections.map((section) => (
          <section key={section.anchor} id={section.anchor} className="flex flex-col gap-[var(--space-4)]">
            <h2 className="text-[length:var(--text-lg)] font-bold text-[var(--text-primary)]">{section.heading}</h2>
            {section.beliefs.map((belief) => (
              <CorrectableBelief key={belief.id} belief={belief} slug={page.slug} onAssert={assert} />
            ))}
          </section>
        ))}

        <AddBelief onAssert={assert} />

        {/* WORLD-1's closed windows. The toggle only exists when there is something behind
            it — a control that reveals nothing is worse than no control. */}
        {page.superseded.length > 0 && (
          <div className="flex flex-col gap-[var(--space-4)]">
            <button
              type="button"
              onClick={() => setShowSuperseded((shown) => !shown)}
              aria-expanded={showSuperseded}
              className="inline-flex w-fit items-center gap-1.5 rounded-full border border-dashed border-[var(--border-default)] px-3 py-1 text-[length:var(--text-xs)] text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
            >
              <History size={12} strokeWidth={2} />
              {showSuperseded ? "Hide superseded" : `Show superseded (${page.superseded.length})`}
            </button>

            {showSuperseded && (
              <section id="superseded" className="flex flex-col gap-[var(--space-4)] opacity-60">
                {page.superseded.map((belief) => (
                  <Belief key={belief.id} belief={belief} slug={page.slug} />
                ))}
              </section>
            )}
          </div>
        )}

        {page.sources.length > 0 && (
          <section id="sources" className="flex flex-col gap-[var(--space-3)]">
            <h2 className="text-[length:var(--text-lg)] font-bold text-[var(--text-primary)]">Sources</h2>
            <ul className="flex flex-col gap-1.5">
              {page.sources.map((source) => (
                <li key={source.sourceMaterialId} className="text-[length:var(--text-sm)]">
                  <Link
                    to={sourceHref(source.kind, source.sourceMaterialId)}
                    className="text-[var(--text-primary)] underline underline-offset-2 hover:text-[var(--vl-life)]"
                  >
                    {source.title ?? "Untitled source"}
                  </Link>
                  <span className="text-[var(--text-muted)]">
                    {source.occurredOn ? ` · ${formatDate(source.occurredOn)}` : ""} · {source.insightCount}{" "}
                    {source.insightCount === 1 ? "insight" : "insights"}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {page.related.length > 0 && (
          <section id="related-subjects" className="flex flex-col gap-[var(--space-3)]">
            <h2 className="text-[length:var(--text-lg)] font-bold text-[var(--text-primary)]">Related subjects</h2>
            <div className="flex flex-wrap gap-2">
              {page.related.map((related) => (
                <Link
                  key={related.slug}
                  to={`/world-view/${related.slug}`}
                  className="rounded-full border border-[var(--border-subtle)] bg-[var(--surface-glass)] px-3 py-1 text-[length:var(--text-xs)] text-[var(--text-secondary)] hover:border-[var(--border-default)] hover:text-[var(--text-primary)]"
                >
                  {related.title}
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* WORLD-7, UI-20 — this subject's relations, editable here as on every other page
            that has them. Absent for a subject that stands for no entity: a relation joins two
            entities, and a topic is not one. */}
        {entityId && page.kind !== "topic" && (
          <div id="relations">
            <RelationsPanel
              thisEntityId={entityId}
              thisEntityName={page.title}
              thisEntityKind={page.kind as EntityType}
              relations={page.relations}
              editable
              candidates={entities}
              onChanged={reload}
            />
          </div>
        )}

        {/* WORLD-5's footer. It says "no remote" rather than leaving a blank, because where
            a knowledge base is published — and whether it is published anywhere off this
            machine — is a fact its reader should have. */}
        <p className="flex flex-wrap items-center gap-1.5 border-t border-[var(--border-subtle)] pt-[var(--space-4)] text-[length:var(--text-2xs)] text-[var(--text-muted)]">
          <GitCommitHorizontal size={12} strokeWidth={2} />
          {git.remote ?? "No remote — the repository is local to the Server"}
          {git.commitSha && (
            <>
              <span aria-hidden>·</span>
              <span className="font-mono">{git.commitSha.slice(0, 7)}</span>
              {git.committedAt && <span>{formatRelative(git.committedAt)}</span>}
            </>
          )}
          {!git.commitSha && (
            <>
              <span aria-hidden>·</span>
              <span>not published yet</span>
            </>
          )}
        </p>
      </div>

      <aside className="sticky top-[var(--space-4)] flex h-fit flex-col gap-[var(--space-6)] text-[length:var(--text-xs)]">
        {(page.sections.length > 0 || page.about) && (
          <nav className="flex flex-col gap-1.5">
            <p className="text-[length:var(--text-2xs)] font-semibold uppercase tracking-[var(--tracking-wider)] text-[var(--text-muted)]">
              On this page
            </p>
            {page.about && (
              <a href="#about" className="text-[var(--text-secondary)] hover:text-[var(--text-primary)]">
                About
              </a>
            )}
            {page.sections.map((section) => (
              // The anchor comes from the Server, so this list and the wiki's `slug#anchor`
              // are the same string — which is the address search and chat citations store.
              <a
                key={section.anchor}
                href={`#${section.anchor}`}
                className="text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
              >
                {section.heading}
              </a>
            ))}
          </nav>
        )}

        {page.history.length > 0 && (
          <div className="flex flex-col gap-1.5">
            <p className="text-[length:var(--text-2xs)] font-semibold uppercase tracking-[var(--tracking-wider)] text-[var(--text-muted)]">
              Page history
            </p>
            {page.history.slice(0, 6).map((revision) => (
              <span key={revision.batchId} className="text-[var(--text-secondary)]">
                {formatRelative(revision.at)} · {revision.verdictCount}{" "}
                {revision.verdictCount === 1 ? "verdict" : "verdicts"} · {revision.insightCount}{" "}
                {revision.insightCount === 1 ? "insight" : "insights"}
                {/* UI-23 — no cost rather than a zero for a batch that made no cloud call, the
                    same rule ARCH-13/UI-22 give every other figure read from the ledger. */}
                {revision.costUsd !== null && <span className="ml-1">· {formatCost(revision.costUsd)}</span>}
                {revision.commitSha && (
                  <span className="ml-1 font-mono text-[var(--text-muted)]">{revision.commitSha.slice(0, 7)}</span>
                )}
              </span>
            ))}
          </div>
        )}

        <div className="flex flex-col gap-1.5">
          <p className="text-[length:var(--text-2xs)] font-semibold uppercase tracking-[var(--tracking-wider)] text-[var(--text-muted)]">
            Wiki file
          </p>
          <span className="break-all font-mono text-[length:var(--text-2xs)] text-[var(--text-secondary)]">
            {page.wikiPath}
          </span>
        </div>
      </aside>
    </div>
  );
}

function BackLink() {
  return (
    <Link
      to="/world-view"
      className="inline-flex w-fit items-center gap-1.5 text-[length:var(--text-xs)] text-[var(--text-muted)] hover:text-[var(--text-primary)]"
    >
      <ArrowLeft size={12} strokeWidth={2} />
      All subjects
    </Link>
  );
}
